/**
 * face-retouch / compare-image.js — одна картинка «до и после».
 *
 * Зачем отдельным файлом. Показать результат ретуши нельзя одним снимком:
 * «после» само по себе выглядит просто как фотография, и человек, который
 * хочет показать разницу, вынужден склеивать два файла руками. Но отдать два
 * файла по одному нажатию мы НЕ можем — браузер даёт ОДИН файл на жест
 * (см. `__downloadFile`: второй в том же жесте придерживается, а лишний
 * отравляет вкладку). Значит либо второй жест, либо одно изображение.
 * Здесь — одно изображение.
 *
 * Решения, которые видно в коде:
 *
 * · **Ориентация склейки выбирается по кадру, а не назначается.** Два
 *   портрета рядом дают почти квадрат; два пейзажа рядом дают полосу 2:1 с
 *   лишним, в которой лицо занимает четверть высоты. Поэтому широкий кадр
 *   складывается СВЕРХУ ВНИЗ, а портретный и квадратный — рядом. Признак —
 *   сам кадр (w > h), а не настройка: настройка здесь была бы вопросом, на
 *   который человеку нечем ответить.
 *
 * · **Подписи выжигаются в картинку.** Смысл выдачи — показать её другому
 *   человеку, часто пересланной; подпись, живущая в интерфейсе, до него не
 *   доедет, и «до» с «после» окажутся неразличимы. Размер подписи считается
 *   от МЕНЬШЕЙ стороны кадра, иначе на 12-мегапиксельном снимке она
 *   превращается в точку.
 *
 * · **Никакого масштабирования исходных пикселей.** Обе половины кладутся
 *   один-в-один: «до/после» — это доказательство, и мыло от ресайза в нём
 *   читается как результат обработки.
 *
 * API:
 *   FRCompare.build(beforeCanvas, afterCanvas, { labelA, labelB }) -> canvas
 */
(function () {
    'use strict';

    var GAP = 0.012;        // зазор между половинами, доля меньшей стороны
    var PAD = 0.028;        // поле подписи
    var MIN_LABEL = 11;     // ниже этого подпись нечитаема даже на превью

    /**
     * @param {HTMLCanvasElement|ImageBitmap} a  кадр «до»
     * @param {HTMLCanvasElement|ImageBitmap} b  кадр «после»
     * @param {{labelA?:string,labelB?:string}} [opts]
     * @returns {HTMLCanvasElement}
     */
    function build(a, b, opts) {
        opts = opts || {};
        var w = a.width, h = a.height;
        if (!w || !h) throw new Error('compare: пустой кадр');
        if (b.width !== w || b.height !== h) {
            // Половины обязаны быть одного размера — иначе это не сравнение.
            throw new Error('compare: половины разного размера');
        }

        var side = Math.min(w, h);
        var gap = Math.max(2, Math.round(side * GAP));
        // Широкий кадр складываем сверху вниз: рядом он даёт полосу, в которой
        // лицо занимает четверть высоты.
        var stacked = w > h;

        var W = stacked ? w : w * 2 + gap;
        var H = stacked ? h * 2 + gap : h;

        var c = document.createElement('canvas');
        c.width = W; c.height = H;
        var ctx = c.getContext('2d');
        // Зазор — не «пусто», а видимая граница: на светлых снимках белый
        // разрыв сливается с кожей и половины кажутся одной картинкой.
        ctx.fillStyle = '#101014';
        ctx.fillRect(0, 0, W, H);

        var posB = stacked ? [0, h + gap] : [w + gap, 0];
        ctx.drawImage(a, 0, 0);
        ctx.drawImage(b, posB[0], posB[1]);

        label(ctx, opts.labelA || '', 0, 0, w, h, side);
        label(ctx, opts.labelB || '', posB[0], posB[1], w, h, side);
        return c;
    }

    /** Подпись в углу половины — на плашке, иначе она тонет в светлом кадре. */
    function label(ctx, text, x, y, w, h, side) {
        if (!text) return;
        var fs = Math.max(MIN_LABEL, Math.round(side * 0.045));
        var pad = Math.round(side * PAD);
        ctx.save();
        ctx.font = '600 ' + fs + 'px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
        ctx.textBaseline = 'middle';
        var tw = ctx.measureText(text).width;
        var bw = tw + fs * 1.1, bh = fs * 1.7;
        var bx = x + pad, by = y + pad;
        ctx.fillStyle = 'rgba(16,16,20,0.72)';
        if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(bx, by, bw, bh, fs * 0.35); ctx.fill(); }
        else ctx.fillRect(bx, by, bw, bh);
        ctx.fillStyle = '#fff';
        ctx.fillText(text, bx + fs * 0.55, by + bh / 2);
        ctx.restore();
    }

    window.FRCompare = { build: build };
})();
